import clsx from "clsx";
import {
  forwardRef,
  lazy,
  Suspense,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { ToastContainer } from "react-toastify";

import type { PartnerTiersTab } from "./modals/PartnerTiersModal";
import type { NotificationKind } from "./statusPresentation";
import type {
  Criterion,
  RebateTier,
  RebateTierWidgetModel,
  TierEvent,
  VolumeUnit,
} from "./types";

import { Button } from "../Button";
import { useIsMobile } from "../hooks/useIsMobile";
import { WarningIllustration } from "../Icons";
import { showErrorToast } from "../Toast";
import { Typo } from "../Typo";
import { countdownDays, daysUntil, isFinalTier } from "./adapter";
import { SectionError } from "./modals/SectionError";
import { SectionSkeleton } from "./modals/SectionSkeleton";
import { RebateTierWidgetSkeleton } from "./RebateTierWidgetSkeleton";
import { CriteriaProgress } from "./sections/CriteriaProgress";
import { ProtectionBadge } from "./sections/ProtectionBadge";
import { TierHeader } from "./sections/TierHeader";
import {
  WidgetNotification,
  type WidgetNotificationEntry,
} from "./sections/WidgetNotification";
import {
  type CountdownSource,
  isQualificationAtRisk,
  resolveStatusPresentation,
} from "./statusPresentation";
import { useRebateTierTranslatedContent } from "./useRebateTierTranslatedContent";

const PartnerTiersModal = lazy(() =>
  import("./modals/PartnerTiersModal").then((m) => ({
    default: m.PartnerTiersModal,
  })),
);
const RebateDetailsModal = lazy(() =>
  import("./modals/RebateDetailsModal").then((m) => ({
    default: m.RebateDetailsModal,
  })),
);
const RebateCalculatorModal = lazy(() =>
  import("./modals/RebateCalculatorModal").then((m) => ({
    default: m.RebateCalculatorModal,
  })),
);
const CriteriaExplainerSheet = lazy(() =>
  import("./modals/CriteriaExplainerSheet").then((m) => ({
    default: m.CriteriaExplainerSheet,
  })),
);
const VolumeConverterSheet = lazy(() =>
  import("./modals/VolumeConverterSheet").then((m) => ({
    default: m.VolumeConverterSheet,
  })),
);
const CycleMobileSheet = lazy(() =>
  import("./modals/CycleMobileSheet").then((m) => ({
    default: m.CycleMobileSheet,
  })),
);

const ProtectionMobileSheet = lazy(() =>
  import("./modals/ProtectionMobileSheet").then((m) => ({
    default: m.ProtectionMobileSheet,
  })),
);

type OpenModal = "partnerTiers" | "rebateDetails" | "rebateCalculator" | null;

// Scoped so the toast never lands in another unscoped ToastContainer on the host page.
const TOAST_CONTAINER_ID = "rebate-tier-widget-toast";

const TIER_EVENT_NOTIFICATION: Record<
  Exclude<TierEvent, null>,
  NotificationKind
> = {
  upgrade: "tierUpgrade",
  downgrade: "tierDowngrade",
  freeze: "tierFreeze",
  unfreeze: "tierUnfreeze",
  grace: "grace",
  atRisk: "qualificationCountdown",
};

const formatGraceEndDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

export type RebateTierWidgetProps = {
  model: RebateTierWidgetModel;
  now?: Date;
  /** Opens the host's "Complete your profile" modal from the profile strip. */
  onCompleteProfile?: () => void;
};

export type RebateTierWidgetHandle = {
  closeRebateDetails: () => void;
};

const countdownTarget = (
  tier: RebateTier,
  from: CountdownSource | null,
): string | null => {
  if (from === "grace") return tier.gracePeriodValidTo;
  if (from === "freeze") return tier.freezePeriodValidTo;
  if (from === "cycle") return tier.cycleEndsAt;
  return null;
};

export const RebateTierWidget = forwardRef<
  RebateTierWidgetHandle,
  RebateTierWidgetProps
>(function RebateTierWidget({ model, now, onCompleteProfile }, ref) {
  const { data, isLoading, isError, refetch } = model.tier;
  const content = useRebateTierTranslatedContent();
  const isMobile = useIsMobile();

  const [openModal, setOpenModal] = useState<OpenModal>(null);
  const [explainCriterion, setExplainCriterion] = useState<Criterion | null>(
    null,
  );
  const [appliedVolumeUnit, setAppliedVolumeUnit] = useState<VolumeUnit | null>(
    null,
  );
  const [dismissedEventNotification, setDismissedEventNotification] =
    useState(false);
  const [savedDefaultOverride, setSavedDefaultOverride] =
    useState<VolumeUnit | null>(null);
  const latestSaveId = useRef(0);
  const [isRetryingVolumeUnits, setIsRetryingVolumeUnits] = useState(false);
  const [isConverterOpen, setIsConverterOpen] = useState(false);
  const [isCycleOpen, setIsCycleOpen] = useState(false);
  const [isProtectionOpen, setIsProtectionOpen] = useState(false);
  const [partnerTiersTab, setPartnerTiersTab] =
    useState<PartnerTiersTab>("overview");

  useImperativeHandle(
    ref,
    () => ({
      closeRebateDetails: () =>
        setOpenModal((current) =>
          current === "rebateDetails" ? null : current,
        ),
    }),
    [],
  );

  if (isLoading) {
    return <RebateTierWidgetSkeleton />;
  }

  if (isError || !data) {
    return (
      <div
        data-testid="rebate-tier-widget-error"
        className="rounded-2xl border border-stroke-primary bg-surface-primary p-6 lg:p-8"
      >
        <div role="alert" className="flex flex-col items-center gap-3">
          <WarningIllustration
            className="h-12 w-12 shrink-0"
            aria-hidden="true"
            focusable="false"
          />
          <Typo
            element="p"
            type="bodyMD"
            textColor="text-text-secondary"
            className="text-center"
          >
            {content.errorStateMessage}
          </Typo>
          <Button
            variant="outline"
            size="md"
            onClick={refetch}
            data-testid="rebate_tier_widget_error_refresh_button"
          >
            {content.errorStateRefresh}
          </Button>
        </div>
      </div>
    );
  }

  const atFinalTier = model.ladder.data
    ? isFinalTier(model.ladder.data, data.tierRank)
    : false;
  const presentation = resolveStatusPresentation(data.status, atFinalTier);
  const criteriaState = model.ladder.data
    ? "ready"
    : model.ladder.isLoading
      ? "loading"
      : "error";
  const showProgress =
    data.tierVisible && presentation.showProgress && model.profileComplete;
  const rawDays = daysUntil(
    countdownTarget(data, presentation.countdownFrom),
    now,
  );
  const daysRemaining = countdownDays(rawDays);
  const isAtRisk =
    data.tierVisible && isQualificationAtRisk(data.status, rawDays);

  const explainedProgress = explainCriterion
    ? data.criteria.find((entry) => entry.criterion === explainCriterion)
    : undefined;

  const openPartnerTiers = (tab: PartnerTiersTab) => {
    setPartnerTiersTab(tab);
    setOpenModal("partnerTiers");
  };

  const closeModal = () => setOpenModal(null);
  const closeExplainer = () => setExplainCriterion(null);
  const closeConverter = () => setIsConverterOpen(false);
  const closeCycle = () => setIsCycleOpen(false);
  const closeProtection = () => setIsProtectionOpen(false);

  // TanStack Query moves a failed query with no data back to `pending` on retry, so keep the error up until the retry settles.
  const converter = {
    ...model.volumeConverter,
    savedDefault: savedDefaultOverride ?? model.volumeConverter.savedDefault,
    isError:
      model.volumeConverter.isError ||
      (isRetryingVolumeUnits && model.volumeConverter.isLoading),
    refetch: () => {
      setIsRetryingVolumeUnits(true);
      model.volumeConverter.refetch?.();
    },
  };
  const volumeUnit = appliedVolumeUnit ?? converter.savedDefault;

  const applyVolumeUnit = (unit: VolumeUnit, saveAsDefault: boolean) => {
    setAppliedVolumeUnit(unit);
    if (!saveAsDefault) return;

    // An earlier save resolving last must not overwrite the newer default.
    const saveId = ++latestSaveId.current;
    const isLatest = () => saveId === latestSaveId.current;
    model.saveDefaultVolumeUnit(unit).then(
      () => {
        if (isLatest()) setSavedDefaultOverride(unit);
      },
      () => {
        if (!isLatest()) return;
        showErrorToast({
          title: content.saveDefaultErrorTitle,
          description: content.saveDefaultErrorBody,
          containerId: TOAST_CONTAINER_ID,
        });
      },
    );
  };

  const tierEventMessages: Record<Exclude<TierEvent, null>, string | null> = {
    upgrade: content.tierUpgradeNotification(data.tierName),
    downgrade: content.tierDowngradeNotification(data.tierName),
    freeze: content.tierFreezeNotification,
    unfreeze: content.tierUnfreezeNotification(data.tierName),
    grace:
      data.status === "grace" &&
      data.gracePeriodValidTo &&
      !data.eventNotification
        ? content.graceActiveNotice(formatGraceEndDate(data.gracePeriodValidTo))
        : null,
    atRisk:
      isAtRisk && rawDays !== null
        ? content.qualificationCountdownNotice(data.tierName, rawDays)
        : null,
  };

  const criterionLabels: Record<Criterion, string> = {
    tv: content.tradingVolume,
    ac: content.activeClients,
    nc: content.newClients,
  };

  const tierNotice = model.notice.data ?? null;
  const tierNoticeMessage = tierNotice
    ? tierEventMessages[tierNotice.event]
    : null;

  const protectionBadge =
    data.status === "frozen" && data.tierVisible ? (
      <ProtectionBadge
        daysRemaining={daysRemaining}
        freezePeriodValidTo={data.freezePeriodValidTo}
        content={content}
        isMobile={isMobile}
        isOpen={isProtectionOpen}
        onOpen={() => setIsProtectionOpen(true)}
      />
    ) : undefined;

  const tierNoticeEntry: WidgetNotificationEntry | null =
    tierNotice && tierNoticeMessage
      ? {
          kind: TIER_EVENT_NOTIFICATION[tierNotice.event],
          message: tierNoticeMessage,
          onDismiss: () => model.notice.dismiss(tierNotice.id),
          dismissLabel: content.close,
        }
      : null;

  const eventNoticeEntry: WidgetNotificationEntry | null =
    data.eventNotification && !dismissedEventNotification
      ? {
          kind: "event",
          message: content.eventNotification(data.tierName),
          onDismiss: () => setDismissedEventNotification(true),
          dismissLabel: content.close,
        }
      : null;

  const completeProfileEntry: WidgetNotificationEntry | null =
    model.profileComplete
      ? null
      : {
          kind: "completeProfile",
          message: content.completeProfile,
          actionLabel: onCompleteProfile ? content.learnMore : undefined,
          onAction: onCompleteProfile,
        };

  const notification: WidgetNotificationEntry | null =
    completeProfileEntry ??
    (data.tierVisible ? (tierNoticeEntry ?? eventNoticeEntry) : null);

  return (
    <>
      <div
        id="rebate-tier-widget"
        data-testid="rebate-tier-widget"
        className="isolate flex flex-1 flex-col overflow-hidden rounded-2xl"
      >
        <div
          className={clsx(
            "relative z-[2] flex flex-col gap-6 rounded-2xl border border-stroke-primary bg-surface-primary p-6 lg:p-8",
            (!data.tierVisible || !model.profileComplete) &&
              "flex-1 justify-center",
          )}
        >
          <div id="rebate-tier-widget-header">
            <TierHeader
              tier={data}
              content={content}
              tierAccessory={protectionBadge}
              onLearnMore={() => openPartnerTiers("overview")}
              onViewDetails={() => setOpenModal("rebateDetails")}
            />
          </div>

          {showProgress && (
            <>
              <div aria-hidden className="h-px w-full bg-stroke-primary" />
              <div id="rebate-tier-widget-progress">
                {criteriaState === "loading" && (
                  <SectionSkeleton
                    rows={2}
                    data-testid="rebate_tier_criteria_loading_message"
                  />
                )}
                {criteriaState === "error" && (
                  <SectionError
                    onRetry={model.ladder.refetch}
                    data-testid="rebate_tier_criteria_error_message"
                    retryTestId="rebate_tier_criteria_retry_button"
                  />
                )}
                {criteriaState === "ready" && (
                  <CriteriaProgress
                    criteria={data.criteria}
                    heading={content[presentation.headingKey]}
                    view={presentation.criteriaView}
                    showUpgradeAffordance={presentation.showUpgradeAffordance}
                    daysRemaining={daysRemaining}
                    isAtRisk={isAtRisk}
                    content={content}
                    volumeUnit={volumeUnit}
                    status={data.status}
                    lastUpdated={data.lastUpdated}
                    cycleEndsAt={data.cycleEndsAt}
                    qualifyingWindow={data.qualifyingWindow}
                    onOpenCriteria={(criterion) => {
                      closeCycle();
                      setExplainCriterion(criterion);
                    }}
                    converter={converter}
                    onApplyVolumeUnit={applyVolumeUnit}
                    isMobile={isMobile}
                    explainedCriterion={explainCriterion}
                    onOpenConverter={() => setIsConverterOpen(true)}
                    isConverterOpen={isConverterOpen}
                    onOpenCycle={() => setIsCycleOpen(true)}
                    isCycleOpen={isCycleOpen}
                    onOpenCycleCriteria={() => openPartnerTiers("criteria")}
                  />
                )}
              </div>
            </>
          )}
        </div>

        <div className="relative z-[1] [&>*:first-child]:-mt-4 [&>*:first-child]:pt-8">
          {notification && <WidgetNotification {...notification} />}
        </div>
      </div>

      {createPortal(
        // react-toastify v11 injects its own stylesheet at the same specificity, so these utilities need `!`.
        <ToastContainer
          containerId={TOAST_CONTAINER_ID}
          position={isMobile ? "top-center" : "top-right"}
          className={clsx(
            "!fixed !z-floating",
            isMobile
              ? "!inset-x-0 !top-20 !mx-auto !w-[calc(100%-2rem)] !max-w-[400px] !transform-none"
              : "!right-4 !top-[5.5rem] !w-[400px]",
          )}
          toastClassName="!w-full !rounded-lg !p-4 !bg-surface-accent-red-subtler"
        />,
        document.body,
      )}

      <Suspense fallback={null}>
        {openModal === "partnerTiers" && (
          <PartnerTiersModal
            isOpen
            initialTab={partnerTiersTab}
            onClose={closeModal}
            isMobile={isMobile}
            tier={data}
            ladder={model.ladder}
            content={content}
            onViewRebateDetails={() => setOpenModal("rebateDetails")}
          />
        )}

        {openModal === "rebateDetails" && (
          <RebateDetailsModal
            isOpen
            onClose={closeModal}
            isMobile={isMobile}
            tier={data}
            ladder={model.ladder}
            rebateDetails={model.rebateDetails}
            content={content}
            onOpenCalculator={() => setOpenModal("rebateCalculator")}
          />
        )}

        {openModal === "rebateCalculator" && (
          <RebateCalculatorModal
            isOpen
            onClose={closeModal}
            isMobile={isMobile}
            tier={data}
            ladder={model.ladder}
            rebateDetails={model.rebateDetails}
            content={content}
          />
        )}

        {isMobile && explainedProgress && (
          <CriteriaExplainerSheet
            isOpen
            onClose={closeExplainer}
            isMobile={isMobile}
            progress={explainedProgress}
            criterionLabel={criterionLabels[explainedProgress.criterion]}
            status={data.status}
            lastUpdated={data.lastUpdated}
            content={content}
          />
        )}

        {isMobile && isConverterOpen && (
          <VolumeConverterSheet
            isOpen
            onClose={closeConverter}
            isMobile={isMobile}
            value={volumeUnit}
            converter={converter}
            content={content}
            onApply={(unit, saveAsDefault) => {
              applyVolumeUnit(unit, saveAsDefault);
              closeConverter();
            }}
          />
        )}

        {isMobile && isCycleOpen && (
          <CycleMobileSheet
            isOpen
            onClose={closeCycle}
            status={data.status}
            cycleEndsAt={data.cycleEndsAt}
            qualifyingWindow={data.qualifyingWindow}
            content={content}
            onOpenCriteria={() => {
              closeCycle();
              openPartnerTiers("criteria");
            }}
          />
        )}

        {isMobile && isProtectionOpen && (
          <ProtectionMobileSheet
            isOpen
            onClose={closeProtection}
            freezePeriodValidTo={data.freezePeriodValidTo}
            content={content}
          />
        )}
      </Suspense>
    </>
  );
});

export * from "./adapter";
export * from "./fixtures";
export * from "./formatVolume";
export * from "./ladder";
export * from "./statusPresentation";
export * from "./tierBadge";
export * from "./transport";
export * from "./types";
export * from "./useRebateTierTranslatedContent";
export * from "./useRebateTierWidgetData";
export { RebateTierWidgetSkeleton } from "./RebateTierWidgetSkeleton";
